import React, { useState, useEffect } from 'react';
import { trackEvent } from '../utils/analytics';
import { useCloudSave } from '../hooks/useCloudSave';
import { useAuth } from '../hooks/useAuth';
import { useQuota } from '../hooks/useQuota';
import { CanvasMetadata } from '../services/cloudSave/canvasMetadata';
import { detectFileFormat, importTreeFromFile } from '../utils/treeImporter';
import { exportTreeToJSON } from '../utils/treeExporter';
import { saveCanvasToCloudAsync, ParallelSaveOptions } from '../utils/parallelSaveUtils';
import { useWhiteboardStore } from '../store/useWhiteboardStore';
import CloudFileBrowser from './CloudFileBrowser';
import ConfirmationDialog from './ConfirmationDialog';
import { AlertTriangle } from 'lucide-react';
import './LoadTreeModal.css';

interface LoadTreeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoad: (fileContent: string, canvasId?: string, canvasName?: string, description?: string, tags?: string[]) => Promise<void>;
}

const LoadTreeModal: React.FC<LoadTreeModalProps> = ({ isOpen, onClose, onLoad }) => {
  const { user } = useAuth();
  const { loadCanvas, deleteCanvas, loadUserCanvases, loading: cloudLoading, clearError, userCanvases, error: cloudError } = useCloudSave();
  const { refreshQuota } = useQuota();
  const { showToast } = useWhiteboardStore();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadMode, setLoadMode] = useState<'local' | 'cloud'>('local');
  const [selectedCanvas, setSelectedCanvas] = useState<CanvasMetadata | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [canvasToDelete, setCanvasToDelete] = useState<CanvasMetadata | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (user) {
        setLoadMode('cloud');
      } else {
        setLoadMode('local');
      }
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (isOpen && user && loadMode === 'cloud') {
      loadUserCanvases();
    }
  }, [isOpen, user, loadMode, loadUserCanvases]);

  useEffect(() => {
    if (isOpen) {
      setSelectedCanvas(null);
      setError(null);
      clearError();
    }
  }, [isOpen, clearError]);

  const handleFileSelect = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json,.yaml,.yml';
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) {
          setIsLoading(false);
          return;
        }
        
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const content = e.target?.result as string;
            await onLoad(content); // No canvasId for local files
            trackEvent('tree_loaded', { tree_name: file.name });
            // Dispatch load event for status indicator
            window.dispatchEvent(new CustomEvent('whiteboard-loaded', { 
              detail: { type: 'local', filename: file.name } 
            }));
            setIsLoading(false);
            onClose();
          } catch (error) {
            setError(error instanceof Error ? error.message : 'Failed to load file');
            setIsLoading(false);
          }
        };
        reader.onerror = () => {
          setError('Failed to read file');
          setIsLoading(false);
        };
        reader.readAsText(file);
      };
      
      input.click();
    } catch (error) {
      setError('Failed to load file');
      setIsLoading(false);
    }
  };

  const handleYamlConversion = async (yamlContent: string, oldCanvasId: string, canvasName: string, description?: string, tags?: string[]) => {
    console.log('🔄 Starting YAML to JSON conversion for canvas:', canvasName, 'ID:', oldCanvasId);
    
    if (!user) {
      console.error('❌ YAML conversion failed: User not logged in');
      showToast('You must be logged in to convert files', 'error');
      return;
    }

    try {
      // 1. Load YAML content into state
      console.log('📖 Stage 1: Loading YAML content into state...');
      const state = await importTreeFromFile(yamlContent, oldCanvasId);
      console.log('✅ Stage 1 complete: YAML content loaded successfully. Notes count:', state.notes.length);
      
      // 2. Convert to JSON
      console.log('🔄 Stage 2: Converting state to JSON format...');
      const jsonContent = exportTreeToJSON(state);
      console.log('✅ Stage 2 complete: JSON conversion successful. Content length:', jsonContent.length, 'characters');
      
      // 3. Save as NEW canvas (no canvasId = new canvas)
      console.log('💾 Stage 3: Saving converted JSON as new canvas...');
      const options: ParallelSaveOptions = {
        userId: user.uid,
        canvasId: undefined, // This creates a new canvas
        canvasName: canvasName || state.canvasName || "Converted Canvas",
        description: description || state.description,
        tags: tags || state.tags
      };
      
      await saveCanvasToCloudAsync(options);
      console.log('✅ Stage 3 complete: New JSON canvas saved successfully');
      
      // 4. Delete old YAML canvas
      console.log('🗑️ Stage 4: Deleting old YAML canvas...');
      const deleteResult = await deleteCanvas(oldCanvasId);
      
      if (deleteResult.success) {
        console.log('✅ Stage 4 complete: Old YAML canvas deleted successfully');
        console.log('🎉 YAML to JSON conversion completed successfully for:', canvasName);
        showToast('Canvas converted to modern format!', 'success');
        trackEvent('yaml_to_json_conversion', { 
          old_canvas_id: oldCanvasId,
          canvas_name: canvasName 
        });
      } else {
        console.warn('⚠️ Stage 4 partial: Old YAML canvas could not be deleted:', deleteResult.error);
        showToast('Canvas converted but old version could not be deleted', 'info');
      }
      
    } catch (error) {
      console.error('❌ YAML conversion failed at stage:', error);
      showToast('Conversion failed, but file loaded successfully', 'error');
    }
  };

  const handleCloudLoad = async (canvas: CanvasMetadata) => {
    try {
      const result = await loadCanvas(canvas.id);
      if (result.success && result.content) {
        // Check if it's YAML and needs conversion
        const fileFormat = detectFileFormat(result.content);
        console.log('🔍 File format detected:', fileFormat, 'for canvas:', canvas.name);
        
        if (fileFormat === 'yaml') {
          console.log('🔄 YAML file detected, starting conversion process...');
          // Convert YAML to JSON
          await handleYamlConversion(
            result.content, 
            canvas.id, 
            canvas.name, 
            canvas.description, 
            canvas.tags
          );
        } else {
          console.log('✅ JSON file detected, no conversion needed');
        }
        
        // Continue with normal load process
        await onLoad(result.content, canvas.id, canvas.name, canvas.description, canvas.tags);
        trackEvent('tree_loaded_from_cloud', { tree_name: canvas.name, canvas_id: canvas.id });
        window.dispatchEvent(new CustomEvent('whiteboard-loaded', { 
          detail: { type: 'cloud', canvasName: canvas.name, canvasId: canvas.id } 
        }));
        onClose();
      } else {
        setError(result.error || 'Failed to load canvas from cloud');
      }
    } catch (error) {
      setError('An unexpected error occurred while loading from cloud');
    }
  };

  const handleCanvasDelete = async (canvas: CanvasMetadata, e: React.MouseEvent) => {
    e.stopPropagation();
    setCanvasToDelete(canvas);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!canvasToDelete) return;
    
    setDeleteLoading(true);
    try {
      const result = await deleteCanvas(canvasToDelete.id);
      if (result.success) {
        // Refresh quota display after successful deletion
        await refreshQuota();
      }
      loadUserCanvases(); // Refresh list
      setShowDeleteConfirm(false);
      setCanvasToDelete(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setCanvasToDelete(null);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="load-tree-modal-backdrop" onClick={handleBackdropClick}>
      <div className="load-tree-modal">
        <div className="load-tree-modal-content">
          <h3>Load Tree</h3>
          <p className="load-tree-description">
            Load a previously saved thread
          </p>

          {/* Load Mode Tabs */}
          <div className="load-mode-tabs">
            <button
              className={`load-mode-tab ${loadMode === 'local' ? 'active' : ''}`}
              onClick={() => setLoadMode('local')}
            >
              💾 Local File
            </button>
            <button
              className={`load-mode-tab ${loadMode === 'cloud' ? 'active' : ''}`}
              onClick={() => setLoadMode('cloud')}
              disabled={!user}
            >
              ☁️ Cloud Saves
            </button>
          </div>

          {/* Error Display */}
          {(error || cloudError) && (
            <div className="load-tree-error">
              {error || cloudError || 'Failed to load canvases'}
            </div>
          )}

          {/* Local Load Section */}
          {loadMode === 'local' && (
            <div className="load-tree-file-section">
              {selectedCanvas ? (
                <div className="selected-file">
                  <span className="file-icon">📄</span>
                  <span className="file-name">{selectedCanvas.name}</span>
                </div>
              ) : (
                <div className="file-drop-zone" onClick={handleFileSelect}>
                  <div className="file-drop-content">
                    <span className="file-drop-icon">📁</span>
                    <p>Click to select a file</p>
                    <p className="file-drop-hint">Supports .json, .yaml and .yml files</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Cloud Load Section */}
          {loadMode === 'cloud' && (
            <CloudFileBrowser
              onCanvasSelect={setSelectedCanvas}
              onCanvasDelete={handleCanvasDelete}
              selectedCanvas={selectedCanvas}
              userCanvases={userCanvases}
              loading={cloudLoading}
              error={cloudError}
            />
          )}

          <div className="load-tree-actions">
            {loadMode === 'local' ? (
              <button 
                className="load-tree-load-btn"
                onClick={handleFileSelect}
                disabled={isLoading}
              >
                {isLoading ? 'Validating Files...' : 'Select File'}
              </button>
            ) : (
              <button 
                className="load-tree-load-btn"
                onClick={() => selectedCanvas && handleCloudLoad(selectedCanvas)}
                disabled={!selectedCanvas || cloudLoading}
              >
                {cloudLoading ? 'Loading...' : 'Load Selected'}
              </button>
            )}
            <button 
              className="load-tree-cancel-btn"
              onClick={onClose}
              disabled={isLoading || cloudLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        title="Delete Canvas"
        message={`Are you sure you want to delete "${canvasToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        icon={<AlertTriangle className="w-5 h-5" />}
        isLoading={deleteLoading}
        loadingText="Deleting..."
      />
    </div>
  );
};

export default LoadTreeModal; 